/**
 * Tagebuch — Reiner Schreibraum (AYU-Design)
 * Design: Sacred Dark / Gold-Akzent (#6b7f4e)
 *
 * Konzept (nach MENTO-Vorbild):
 * 1. Textfeld ist DAS Hauptelement — groß, prominent, zentriert
 * 2. AYU schweigt komplett — kein Auto-Coaching nach dem Speichern
 * 3. Coaching-Button prominent sichtbar — "AYU um Coaching bitten"
 * 4. Verlauf als Seiten zum Blättern (Prev/Next)
 * 5. Trigger: /orakel, /mantra, /joke nur auf expliziten Klick
 * 6. Stimmungs-Emojis vor dem Speichern
 */
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useStreamingChat } from "@/hooks/useStreamingChat";
import { useClientData } from "@/hooks/useClientData";
import { trpc } from "@/lib/trpc";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useSpeechSynthesis } from "@/hooks/useSpeechSynthesis";
import {
  getDailyQuote,
  getDailyJoke,
  getZodiacSign,
  getDailyHoroscope,
  extractBirthDate,
  parseBirthdateString,
  type DailyOption,
} from "@/config/dailyInspiration";
import {
  Send,
  BookOpen,
  Calendar,
  Sparkles,
  AlertCircle,
  RotateCcw,
  Mic,
  MicOff,
  Loader2,
  PenLine,
  MessageCircle,
  Save,
  Volume2,
  VolumeX,
  Star,
  Quote,
  Laugh,
  Compass,
  Flame,
  SmilePlus,
  CalendarDays,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
} from "lucide-react";
import CrisisAlert, { containsCrisisKeywords } from "@/components/app/CrisisAlert";
import DoshaModeIndicator from "@/components/app/DoshaModeIndicator";
import ChatMarkdown from "@/components/app/ChatMarkdown";
import SaveRecipeButton from "@/components/app/SaveRecipeButton";

import { createLogger } from "@/lib/logger";
import { parseDbDate } from "@/lib/utils";
import { useI18n } from "@/contexts/I18nContext";
const log = createLogger("Apptagebuch");

interface Message {
  id: string;
  role: "user" | "ayu";
  text: string;
  time: string;
}

type ViewMode = "writing" | "saved" | "coaching" | "history";

interface MemoryContextData {
  summaries: { id: number; chatType: string; title: string | null; summary: string; mood: string | null; createdAt: string | Date }[];
  recentSessions: {
    ayu: { sessionId: number; title: string | null; createdAt: string | Date; messages: { role: string; content: string; createdAtMs: number }[] }[];
    tagebuch: { sessionId: number; title: string | null; mood?: string | null; createdAt: string | Date; messages: { role: string; content: string; createdAtMs: number }[] }[];
  };
}

function buildMemoryBlock(memoryData: MemoryContextData | undefined): string {
  if (!memoryData) return "";
  let block = "";

  if (memoryData.summaries.length > 0) {
    block += "\n\n═══ GEDÄCHTNIS: FRÜHERE GESPRÄCHE ═══";
    block += "\nDu erinnerst dich an alle bisherigen Gespräche. Nutze dieses Wissen natürlich, ohne es aufzuzählen.";
    block += "\nWenn ein Thema wieder auftaucht, knüpfe nahtlos daran an.\n";
    for (const s of memoryData.summaries) {
      const date = parseDbDate(s.createdAt).toLocaleDateString("de-DE", { day: "numeric", month: "short" });
      const type = s.chatType === "tagebuch" ? "Tagebuch" : "Coaching";
      const moodTag = s.mood ? ` [Stimmung: ${s.mood}]` : "";
      block += `\n[${date} — ${type}${moodTag}]\n${s.summary}\n`;
    }
  }

  const recentAll = [
    ...memoryData.recentSessions.ayu.map((s) => ({ ...s, type: "Coaching" as const })),
    ...memoryData.recentSessions.tagebuch.map((s) => ({ ...s, type: "Tagebuch" as const })),
  ].sort((a, b) => parseDbDate(a.createdAt).getTime() - parseDbDate(b.createdAt).getTime());

  if (recentAll.length > 0) {
    block += "\n\n═══ LETZTE GESPRÄCHE (DETAILS) ═══";
    for (const session of recentAll) {
      const date = parseDbDate(session.createdAt).toLocaleDateString("de-DE", { day: "numeric", month: "short" });
      block += `\n--- ${session.type} vom ${date} ---`;
      for (const m of session.messages.slice(-8)) {
        const speaker = m.role === "user" ? "Nutzer" : "Annam";
        const text = m.content.length > 200 ? m.content.slice(0, 200) + "…" : m.content;
        block += `\n${speaker}: ${text}`;
      }
    }
  }

  return block;
}

function buildApiMessages(systemPrompt: string, messages: Message[], memoryContext?: MemoryContextData, documentContext?: string) {
  const memoryBlock = buildMemoryBlock(memoryContext);
  const docBlock = documentContext
    ? `\n\n═══ HOCHGELADENE DOKUMENTE ═══\nNutze diese Informationen als Hintergrundwissen, wenn relevant.\n\n${documentContext}`
    : "";
  const fullSystemPrompt = systemPrompt + memoryBlock + docBlock;

  const apiMessages: { role: "system" | "user" | "assistant"; content: string }[] = [
    { role: "system", content: fullSystemPrompt },
  ];
  for (const msg of messages) {
    if (msg.role === "user") {
      apiMessages.push({ role: "user", content: msg.text });
    } else {
      apiMessages.push({ role: "assistant", content: msg.text });
    }
  }
  return apiMessages;
}

function formatTime(ms?: number) {
  if (!ms) return new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return new Date(ms).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function formatDate() {
  return new Date().toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatFullTimestamp() {
  const now = new Date();
  return now.toLocaleString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSessionDate(date: Date | string, locale: string, t: (key: string, params?: Record<string, string>) => string): string {
  const d = parseDbDate(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t("common.today");
  if (diffDays === 1) return t("common.yesterday");
  if (diffDays < 7) return t("common.daysAgo", { days: String(diffDays) });
  return d.toLocaleDateString(locale === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "long", year: "numeric" });
}

export default function AppTagebuch() {
  const { t, locale } = useI18n();
  const clientData = useClientData();
  const [viewMode, setViewMode] = useState<ViewMode>("writing");
  const [journalText, setJournalText] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showCrisisAlert, setShowCrisisAlert] = useState(false);
  const [activeOption, setActiveOption] = useState<DailyOption>("quote");
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [birthdayInput, setBirthdayInput] = useState("");
  const [birthdaySaving, setBirthdaySaving] = useState(false);

  // History pagination
  const [historyPage, setHistoryPage] = useState(0);
  const [viewingSessionId, setViewingSessionId] = useState<number | null>(null);
  const [viewingMessages, setViewingMessages] = useState<Message[] | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  const saveMoodMutation = trpc.chat.saveMood.useMutation();
  // Streaming-Chat: Token-by-Token Anzeige
  const streamingAyuMsgRef = useRef<string>("");
  const streamingChat = useStreamingChat({
    onToken: (_token, fullText) => {
      streamingAyuMsgRef.current = fullText;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.id === "streaming-ayu") {
          // Only update if text actually changed (prevents unnecessary re-renders)
          if (last.text === fullText) return prev;
          const next = prev.slice();
          next[next.length - 1] = { ...last, text: fullText };
          return next;
        }
        return prev;
      });
    },
  });
  const chatMutation = trpc.chat.send.useMutation();
  const saveBirthdateMutation = trpc.chat.saveBirthdate.useMutation();
  const utils = trpc.useUtils();
  const voice = useVoiceInput(clientData.clientId || "anon");
  const tts = useSpeechSynthesis({ voicePreference: clientData.voicePreference as "annam" | "christopher" | undefined });

  const birthdateQuery = trpc.chat.getBirthdate.useQuery(
    { clientId: clientData.clientId || "anon" },
    { enabled: !!clientData.clientId }
  );

  const todayFormatted = formatDate();
  const timestampFormatted = formatFullTimestamp();

  // Tages-Optionen
  const dailyQuote = useMemo(() => getDailyQuote(), []);
  const dailyJoke = useMemo(() => getDailyJoke(), []);
  const zodiacInfo = useMemo(() => {
    if (birthdateQuery.data?.birthdate) {
      const parsed = parseBirthdateString(birthdateQuery.data.birthdate);
      if (parsed) {
        const sign = getZodiacSign(parsed.month, parsed.day);
        if (sign) {
          const horoscope = getDailyHoroscope(sign.name);
          return { ...sign, horoscope };
        }
      }
    }
    const birthDate = extractBirthDate(clientData.anamneseData);
    if (!birthDate) return null;
    const sign = getZodiacSign(birthDate.month, birthDate.day);
    if (!sign) return null;
    const horoscope = getDailyHoroscope(sign.name);
    return { ...sign, horoscope };
  }, [clientData.anamneseData, birthdateQuery.data]);

  // Stabiler Query-Input
  const historyInput = useMemo(() => ({
    clientId: clientData.clientId || "anon",
    chatType: "tagebuch" as const,
  }), [clientData.clientId]);

  const { data: historyData, isFetched: historyFetched } = trpc.chat.getHistory.useQuery(historyInput, {
    enabled: !!clientData.clientId,
    refetchOnWindowFocus: false,
    staleTime: 0, // Immer frisch laden wenn die Komponente mountet
  });

  // Sessions für Verlauf
  const sessionsInput = useMemo(() => ({
    clientId: clientData.clientId || "anon",
    chatType: "tagebuch" as const,
  }), [clientData.clientId]);

  const { data: sessions } = trpc.chat.getSessions.useQuery(sessionsInput, {
    enabled: !!clientData.clientId,
    refetchOnWindowFocus: false,
  });

  // Letzte 5 Tage Tagebuch-Einträge für Coaching-Kontext
  const recentEntriesInput = useMemo(() => ({
    clientId: clientData.clientId || "anon",
    days: 5,
  }), [clientData.clientId]);

  const { data: recentDiaryEntries } = trpc.chat.getRecentDiaryEntries.useQuery(recentEntriesInput, {
    enabled: !!clientData.clientId,
    refetchOnWindowFocus: false,
  });

  // Gedächtnis-Kontext laden
  const memoryInput = useMemo(() => ({
    clientId: clientData.clientId || "anon",
  }), [clientData.clientId]);

  const { data: memoryContext } = trpc.chat.getMemoryContext.useQuery(memoryInput, {
    enabled: !!clientData.clientId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Dokument-Zusammenfassungen laden
  const docSummariesInput = useMemo(() => ({
    clientId: clientData.clientId || "anon",
  }), [clientData.clientId]);

  const { data: documentSummaries } = trpc.documents.summaries.useQuery(docSummariesInput, {
    enabled: !!clientData.clientId,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  const closedSessions = useMemo(
    () => (sessions || []).filter((s) => s.status === "closed"),
    [sessions]
  );

  // Verlauf aus DB laden — IMMER im Schreibmodus starten
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [existingMessages, setExistingMessages] = useState<Message[]>([]);

  useEffect(() => {
    if (!historyFetched || !historyData || historyLoaded) return;

    if (historyData.messages.length > 0) {
      const loadedMessages: Message[] = historyData.messages
        .filter((m) => m.role !== "system")
        .map((m) => ({
          id: `db-${m.id}`,
          role: m.role === "user" ? "user" as const : "ayu" as const,
          text: m.content,
          time: formatTime(m.createdAtMs),
        }));

      const hasUserEntry = loadedMessages.some((m) => m.role === "user");
      if (hasUserEntry) {
        setExistingMessages(loadedMessages);
        setHasExistingSession(true);
        setViewMode("writing");
      }
    }
    setHistoryLoaded(true);
    // Nach dem Laden von getHistory die Sessions-Liste invalidieren,
    // damit frisch geschlossene (stale) Sessions im Archiv erscheinen
    utils.chat.getSessions.invalidate();
  }, [historyFetched, historyData, historyLoaded, utils]);

  // Throttled auto-scroll: only scroll when message count changes
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    const currentCount = messages.length;
    const hasStreamingMsg = messages.some(m => m.id === "streaming-ayu");
    if (currentCount !== prevMsgCountRef.current || isTyping) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      prevMsgCountRef.current = currentCount;
    } else if (hasStreamingMsg) {
      const container = chatEndRef.current?.parentElement;
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
        if (isNearBottom) {
          chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }, [messages, isTyping]);

  // Load old session for page-based history
  const loadSessionQuery = trpc.chat.loadSession.useQuery(
    { sessionId: viewingSessionId! },
    { enabled: !!viewingSessionId, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (!loadSessionQuery.data || !viewingSessionId) return;
    const loaded: Message[] = loadSessionQuery.data.messages
      .filter((m) => m.role !== "system")
      .map((m) => ({
        id: `old-${m.id}`,
        role: m.role === "user" ? "user" as const : "ayu" as const,
        text: m.content,
        time: formatTime(m.createdAtMs),
      }));
    setViewingMessages(loaded);
  }, [loadSessionQuery.data, viewingSessionId]);

  // -- Eintrag speichern (NUR speichern, KEIN AYU) --
  const handleSaveEntry = useCallback(async () => {
    if (!journalText.trim() || !clientData.clientId) return;
    setError(null);

    if (containsCrisisKeywords(journalText.trim())) {
      setShowCrisisAlert(true);
    }

    const entryWithTimestamp = `[${timestampFormatted}]\n\n${journalText.trim()}`;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      text: entryWithTimestamp,
      time: formatTime(),
    };

    setMessages([userMsg]);
    setViewMode("saved");

    try {
      await chatMutation.mutateAsync({
        messages: [
          { role: "system", content: "Tagebuch-Eintrag. Antworte nur mit: Eintrag gespeichert." },
          { role: "user", content: entryWithTimestamp },
        ],
        clientId: clientData.clientId,
        chatType: "tagebuch",
      });

      if (selectedMood) {
        await saveMoodMutation.mutateAsync({
          clientId: clientData.clientId,
          mood: selectedMood,
        });
      }

      utils.chat.getSessions.invalidate();
      utils.chat.getHistory.invalidate();
      utils.chat.getRecentDiaryEntries.invalidate();
    } catch (err) {
      log.error("Save error:");
      setError(t("tagebuch.fehlerGespeichert"));
      setViewMode("writing");
    }
  }, [journalText, clientData.clientId, chatMutation, saveMoodMutation, selectedMood, timestampFormatted, utils]);

  // -- AYU um Coaching bitten (nur auf expliziten Klick) --
  const handleRequestCoaching = useCallback(async () => {
    if (!clientData.clientId || messages.length === 0) return;
    setViewMode("coaching");
    setIsTyping(true);
    setError(null);

    try {
      let diaryContext = "";
      if (recentDiaryEntries && recentDiaryEntries.length > 0) {
        const entrySummaries = recentDiaryEntries.map((e) => {
          const date = new Date(e.createdAtMs).toLocaleDateString("de-DE", {
            weekday: "short",
            day: "numeric",
            month: "short",
          });
          const moodLabel = e.mood ? ` [Stimmung: ${e.mood}]` : "";
          return `${date}${moodLabel}: ${e.content}`;
        });
        diaryContext = `\n\n=== TAGEBUCH-EINTRÄGE DER LETZTEN 5 TAGE ===\n${entrySummaries.join("\n\n")}\n=== ENDE TAGEBUCH-KONTEXT ===\n\nAnalysiere besonders den aktuellsten Eintrag. Erkenne Muster und Entwicklungen über die Tage.`;
      }

      const enrichedPrompt = clientData.tagebuchPrompt + diaryContext;
      const apiMessages = buildApiMessages(enrichedPrompt, messages, memoryContext as MemoryContextData | undefined, documentSummaries || undefined);
      apiMessages.push({
        role: "user",
        content: "Ich möchte gerne dein Coaching zu meinem Tagebuch-Eintrag. Schau dir an, was ich geschrieben habe, und gib mir eine ehrliche, tiefgründige Rückmeldung basierend auf meiner Anamnese und meinem Profil.",
      });

      const streamingMsg: Message = { id: "streaming-ayu", role: "ayu", text: "", time: formatTime() };
      setMessages((prev) => [...prev, streamingMsg]);
      streamingAyuMsgRef.current = "";

      const result = await streamingChat.sendStreaming({
        messages: apiMessages,
        clientId: clientData.clientId,
        chatType: "tagebuch",
      });

      setMessages((prev) => {
        const withoutStreaming = prev.filter((m) => m.id !== "streaming-ayu");
        return [...withoutStreaming, { id: `ayu-${Date.now()}`, role: "ayu" as const, text: result.content, time: formatTime() }];
      });
    } catch (err) {
      log.error("Coaching error:");
      setError(t("tagebuch.fehlerCoaching"));
      setMessages((prev) => prev.filter((m) => m.id !== "streaming-ayu"));
    } finally {
      setIsTyping(false);
    }
  }, [clientData.clientId, clientData.tagebuchPrompt, messages, streamingChat, recentDiaryEntries, memoryContext, documentSummaries]);

  // -- Trigger-Funktionen (/orakel, /mantra, /joke) --
  const handleTrigger = useCallback(async (trigger: "orakel" | "mantra" | "joke") => {
    if (!clientData.clientId) return;
    setViewMode("coaching");
    setIsTyping(true);
    setError(null);

    const triggerLabels = { orakel: "Orakel", mantra: "Mantra", joke: "Witz" };

    const triggerMsg: Message = {
      id: `user-trigger-${Date.now()}`,
      role: "user",
      text: `/${trigger}`,
      time: formatTime(),
    };

    const updatedMessages = [...messages, triggerMsg];
    setMessages(updatedMessages);

    try {
      const apiMessages = buildApiMessages(clientData.tagebuchPrompt, updatedMessages, memoryContext as MemoryContextData | undefined, documentSummaries || undefined);
      apiMessages.push({
        role: "user",
        content: `[${trigger.toUpperCase()}] — Bitte gib mir mein ${triggerLabels[trigger]} für heute.`,
      });

      const streamingMsg: Message = { id: "streaming-ayu", role: "ayu", text: "", time: formatTime() };
      setMessages((prev) => [...prev, streamingMsg]);
      streamingAyuMsgRef.current = "";

      const result = await streamingChat.sendStreaming({
        messages: apiMessages,
        clientId: clientData.clientId,
        chatType: "tagebuch",
      });

      setMessages((prev) => {
        const withoutStreaming = prev.filter((m) => m.id !== "streaming-ayu");
        return [...withoutStreaming, { id: `ayu-trigger-${Date.now()}`, role: "ayu" as const, text: result.content, time: formatTime() }];
      });
    } catch (err) {
      log.error("... error:");
      setError(`${triggerLabels[trigger]} konnte nicht geladen werden.`);
      setMessages((prev) => prev.filter((m) => m.id !== "streaming-ayu"));
    } finally {
      setIsTyping(false);
    }
  }, [clientData.clientId, clientData.tagebuchPrompt, messages, streamingChat, memoryContext, documentSummaries]);

  // -- Chat-Nachricht senden (im Coaching-Modus) --
  const handleSendChat = useCallback(async () => {
    if (!chatInput.trim() || isTyping || !clientData.clientId) return;
    setError(null);

    if (containsCrisisKeywords(chatInput.trim())) {
      setShowCrisisAlert(true);
    }

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      text: chatInput.trim(),
      time: formatTime(),
    };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setChatInput("");
    setIsTyping(true);

    try {
      const apiMessages = buildApiMessages(clientData.tagebuchPrompt, updatedMessages, memoryContext as MemoryContextData | undefined, documentSummaries || undefined);

      const streamingMsg: Message = { id: "streaming-ayu", role: "ayu", text: "", time: formatTime() };
      setMessages((prev) => [...prev, streamingMsg]);
      streamingAyuMsgRef.current = "";

      const result = await streamingChat.sendStreaming({
        messages: apiMessages,
        clientId: clientData.clientId,
        chatType: "tagebuch",
      });

      setMessages((prev) => {
        const withoutStreaming = prev.filter((m) => m.id !== "streaming-ayu");
        return [...withoutStreaming, { id: `ayu-${Date.now()}`, role: "ayu" as const, text: result.content, time: formatTime() }];
      });
    } catch (err) {
      log.error("Chat error:");
      setError(t("errors.allgemein"));
      setMessages((prev) => prev.filter((m) => m.id !== "streaming-ayu"));
    } finally {
      setIsTyping(false);
    }
  }, [chatInput, isTyping, messages, streamingChat, clientData.clientId, clientData.tagebuchPrompt, memoryContext, documentSummaries]);

  const handleChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  };

  // -- Voice Input --
  const handleVoiceToggle = useCallback(async () => {
    if (voice.isRecording) {
      const text = await voice.stopRecording();
      if (text) {
        if (viewMode === "writing") {
          setJournalText((prev) => (prev ? prev + " " + text : text));
          textareaRef.current?.focus();
        } else {
          setChatInput((prev) => (prev ? prev + " " + text : text));
          chatInputRef.current?.focus();
        }
      }
    } else {
      await voice.startRecording();
    }
  }, [voice, viewMode]);

  // -- Session Management --
  const newSessionMutation = trpc.chat.newSession.useMutation();

  const handleNewSession = useCallback(async () => {
    if (!clientData.clientId) return;

    // Reset UI instantly — don't wait for the network call
    setMessages([]);
    setJournalText("");
    setViewMode("writing");
    setSelectedMood(null);
    setHasExistingSession(false);
    setExistingMessages([]);
    setViewingSessionId(null);
    setViewingMessages(null);
    setHistoryPage(0);
    // Keep historyLoaded=true so the useEffect doesn't re-run on refetch
    setHistoryLoaded(true);

    // Fire mutation in background, then refresh only the sessions list
    newSessionMutation.mutateAsync({
      clientId: clientData.clientId,
      chatType: "tagebuch",
    }).then(() => {
      utils.chat.getSessions.invalidate();
      utils.chat.getHistory.invalidate();
    }).catch(() => {});
  }, [newSessionMutation, utils, clientData.clientId]);

  // History page navigation
  const handleViewHistory = useCallback(() => {
    if (closedSessions.length === 0) return;
    setHistoryPage(0);
    const firstSession = closedSessions[0];
    setViewingSessionId(firstSession.id);
    setViewingMessages(null);
    setViewMode("history");
  }, [closedSessions]);

  const handleHistoryPrev = useCallback(() => {
    if (historyPage <= 0) return;
    const newPage = historyPage - 1;
    setHistoryPage(newPage);
    setViewingSessionId(closedSessions[newPage].id);
    setViewingMessages(null);
  }, [historyPage, closedSessions]);

  const handleHistoryNext = useCallback(() => {
    if (historyPage >= closedSessions.length - 1) return;
    const newPage = historyPage + 1;
    setHistoryPage(newPage);
    setViewingSessionId(closedSessions[newPage].id);
    setViewingMessages(null);
  }, [historyPage, closedSessions]);

  const handleBackFromHistory = useCallback(() => {
    setViewingSessionId(null);
    setViewingMessages(null);
    const hasAyuReply = messages.some((m) => m.role === "ayu");
    const hasUserEntry = messages.some((m) => m.role === "user");
    if (hasAyuReply) {
      setViewMode("coaching");
    } else if (hasUserEntry) {
      setViewMode("saved");
    } else {
      setViewMode("writing");
    }
  }, [messages]);

  // Lade-Zustand
  if (clientData.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-500">
      {/* Header */}
      <div className="pb-3 border-b border-[#d4dcc4] px-4 pt-4 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#6b7f4e]/10 flex items-center justify-center">
              <BookOpen className="w-[18px] h-[18px] text-[#6b7f4e]" />
            </div>
            <div>
              <h1 className="font-['Playfair_Display'] text-xl font-semibold text-[#2d3a1e]">
                {t("nav.tagebuch")}
              </h1>
              <p className="text-[11px] text-[#8a9680] mt-0.5 hidden sm:block">{t("tagebuch.subtitle")}</p>
            </div>
          </div>
          <button
            onClick={handleNewSession}
            className="text-xs text-[#5a6b4a] hover:text-[#6b7f4e] transition-colors flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[#6b7f4e]/5"
            title={t("tagebuch.neuerEintrag")}
          >
            <RotateCcw className="w-3 h-3" />
            <span className="hidden sm:inline">{t("tagebuch.neuerEintrag")}</span>
          </button>
        </div>

        {/* Tages-Navigation mit Pfeilen */}
        <div className="flex items-center justify-center gap-3 mt-2">
          <button
            onClick={() => {
              if (viewMode === "history") {
                handleHistoryNext();
              } else if (closedSessions.length > 0) {
                setHistoryPage(0);
                setViewingSessionId(closedSessions[0].id);
                setViewingMessages(null);
                setViewMode("history");
              }
            }}
            disabled={viewMode === "history" && historyPage >= closedSessions.length - 1}
            className={`p-2 rounded-xl transition-all duration-200 ${
              (viewMode !== "history" && closedSessions.length > 0) || (viewMode === "history" && historyPage < closedSessions.length - 1)
                ? "text-[#6b7f4e] bg-[#6b7f4e]/10 hover:bg-[#6b7f4e]/20 active:scale-95"
                : "text-[#5a6b4a]/20 cursor-not-allowed"
            }`}
            title={t("tagebuch.vorherigTag")}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="text-center min-w-0 flex-1">
            {viewMode === "history" && closedSessions[historyPage] ? (
              <>
                <p className="text-sm font-medium text-[#2d3a1e]">
                  {parseDbDate(closedSessions[historyPage].createdAt).toLocaleDateString(locale === "en" ? "en-GB" : "de-DE", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
                <p className="text-[10px] text-[#8a9680]">
                  {t("tagebuch.eintragNum", { current: String(historyPage + 1), total: String(closedSessions.length) })}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-[#2d3a1e] flex items-center justify-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-[#6b7f4e]" />
                  {todayFormatted}
                </p>
                {closedSessions.length > 0 && (
                  <p className="text-[10px] text-[#8a9680]">
                    {t("tagebuch.vergangeneEintraege", { count: String(closedSessions.length) })}
                  </p>
                )}
              </>
            )}
          </div>

          <button
            onClick={() => {
              if (viewMode === "history") {
                if (historyPage <= 0) {
                  handleBackFromHistory();
                } else {
                  handleHistoryPrev();
                }
              }
            }}
            disabled={viewMode !== "history"}
            className={`p-2 rounded-xl transition-all duration-200 ${
              viewMode === "history"
                ? "text-[#6b7f4e] bg-[#6b7f4e]/10 hover:bg-[#6b7f4e]/20 active:scale-95"
                : "text-[#5a6b4a]/20 cursor-not-allowed"
            }`}
            title={viewMode === "history" && historyPage <= 0 ? t("tagebuch.zurueckZuHeute") : t("tagebuch.naechsterTag")}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Sicherheits-Layer */}
      <CrisisAlert
        visible={showCrisisAlert}
        onDismiss={() => setShowCrisisAlert(false)}
        accentColor="#6b7f4e"
      />

      <DoshaModeIndicator
        doshaType={clientData.hasAnamnese ? "ayu-phänotyp" : undefined}
        visible={clientData.hasAnamnese}
        className="mx-4 mt-2"
      />

      {/* ═══════════════════════════════════════════════════ */}
      {/* SCHREIBMODUS — Textfeld als Hauptelement          */}
      {/* ═══════════════════════════════════════════════════ */}
      {viewMode === "writing" && (
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">

          {/* Collapsible top: existing session banner + daily tabs */}
          <div className="shrink-0 overflow-y-auto px-4 max-h-[140px] sm:max-h-[220px]">
            {hasExistingSession && (
              <div className="mt-3 mb-2 mx-auto max-w-2xl w-full">
                <div className="bg-[#6b7f4e]/5 border border-[#d4dcc4] rounded-2xl p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-[#6b7f4e]/60" />
                    <span className="text-xs text-[#5a6b4a]">{t("tagebuch.laufenderEintrag")}</span>
                  </div>
                  <button
                    onClick={() => {
                      setMessages(existingMessages);
                      const hasAyuReply = existingMessages.some((m) => m.role === "ayu");
                      setViewMode(hasAyuReply ? "coaching" : "saved");
                    }}
                    className="text-xs font-medium text-[#6b7f4e] hover:text-[#b8963d] transition-colors px-2 py-1 rounded-lg hover:bg-[#6b7f4e]/10"
                  >
                    {t("common.continue")}
                  </button>
                </div>
              </div>
            )}

            {/* Tages-Optionen Tabs */}
            <div className="mt-2 mb-2 mx-auto max-w-2xl w-full">
              <div className="flex gap-1 sm:gap-1.5 mb-1.5">
                {(["quote", "horoscope", "joke"] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setActiveOption(opt)}
                    className={`flex-1 flex items-center justify-center gap-1 sm:gap-1.5 py-1 sm:py-1.5 px-2 sm:px-3 rounded-xl text-[11px] sm:text-xs font-medium transition-all duration-200 ${
                      activeOption === opt
                        ? opt === "joke"
                          ? "bg-[#E8A87C]/15 text-[#E8A87C] shadow-sm"
                          : "bg-[#6b7f4e]/15 text-[#6b7f4e] shadow-sm"
                        : "text-[#5a6b4a]/50 hover:text-[#5a6b4a] hover:bg-[#6b7f4e]/5"
                    }`}
                  >
                    {opt === "quote" && <Quote className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                    {opt === "horoscope" && <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                    {opt === "joke" && <Laugh className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                    <span className="hidden sm:inline">
                      {opt === "quote" ? t("tagebuch.spruch") : opt === "horoscope" ? t("tagebuch.horoskop") : t("tagebuch.witz")}
                    </span>
                  </button>
                ))}
              </div>
              <div className="transition-all duration-300">
                {activeOption === "quote" && (
                  <div className="bg-[#f0f3eb] rounded-2xl border border-[#d4dcc4] p-2.5 sm:p-4 text-center">
                    <Quote className="w-3 h-3 text-[#6b7f4e]/30 mx-auto mb-1" />
                    <p className="font-['Playfair_Display'] text-sm sm:text-base italic text-[#2d3a1e]/70 leading-snug sm:leading-relaxed line-clamp-2 sm:line-clamp-none">
                      &ldquo;{dailyQuote.text}&rdquo;
                    </p>
                    <p className="text-[10px] sm:text-xs text-[#5a6b4a]/50 mt-0.5 sm:mt-1">&mdash; {dailyQuote.author}</p>
                  </div>
                )}
                {activeOption === "horoscope" && (
                  <div className="bg-gradient-to-r from-[#f0f3eb] to-[#e8eee0] rounded-2xl border border-[#d4dcc4] p-2.5 sm:p-4">
                    {zodiacInfo ? (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#6b7f4e]/10 flex items-center justify-center shrink-0">
                          <span className="text-xl">{zodiacInfo.symbol}</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Star className="w-3 h-3 text-[#6b7f4e]" />
                            <span className="text-xs font-medium text-[#6b7f4e]">{zodiacInfo.name}</span>
                          </div>
                          <p className="text-sm text-[#2d3a1e]/60 leading-relaxed">{zodiacInfo.horoscope}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <Star className="w-5 h-5 text-[#6b7f4e]/40 mx-auto mb-2" />
                        <p className="text-sm text-[#2d3a1e]/50 mb-1">{t("tagebuch.sternzeichenUnbekannt")}</p>
                        <button
                          onClick={() => setShowBirthdayModal(true)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#6b7f4e]/15 text-[#6b7f4e] text-xs font-medium hover:bg-[#6b7f4e]/25 transition-all duration-200"
                        >
                          <CalendarDays className="w-3.5 h-3.5" />
                          {t("tagebuch.geburtsdatumEingeben")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {activeOption === "joke" && (
                  <div className="bg-gradient-to-r from-[#f0f3eb] to-[#e8eee0] rounded-2xl border border-[#E8A87C]/10 p-2.5 sm:p-4 text-center">
                    <Laugh className="w-3.5 h-3.5 text-[#E8A87C]/40 mx-auto mb-1" />
                    <p className="text-xs sm:text-sm text-[#2d3a1e]/70 leading-snug sm:leading-relaxed line-clamp-2 sm:line-clamp-none">{dailyJoke}</p>
                    <p className="text-[10px] text-[#E8A87C]/40 mt-1">{t("tagebuch.cortisol")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Time */}
          <div className="shrink-0 text-center py-1.5 px-4">
            <p className="text-xs text-[#5a6b4a]/40">{formatTime()} {t("tagebuch.uhr")}</p>
          </div>

          {/* Textarea — fills all remaining space */}
          <div className="flex-1 px-4 pb-2 min-h-0">
            <div className="bg-[#f0f3eb] rounded-2xl border border-[#d4dcc4] shadow-sm p-4 h-full mx-auto max-w-2xl w-full flex flex-col">
              <textarea
                ref={textareaRef}
                value={journalText}
                onChange={(e) => setJournalText(e.target.value)}
                placeholder={t("tagebuch.eintragSchreiben")}
                className="flex-1 w-full resize-none bg-transparent text-base text-[#2d3a1e] leading-relaxed placeholder:text-[#5a6b4a]/30 focus:outline-none font-['DM_Sans',sans-serif]"
                autoFocus
              />
            </div>
          </div>

          {/* Pinned bottom: Mood row + Action bar */}
          <div className="shrink-0 px-4 pt-2 pb-safe pb-3 bg-[#f8f8f8]">
            {/* Mood row */}
            <div className="flex items-center justify-center gap-1 sm:gap-3 mb-2 mx-auto max-w-2xl w-full">
              <span className="text-[10px] text-[#5a6b4a]/50 shrink-0 mr-1">{t("tagebuch.wieGefuehlst")}</span>
              {[
                { key: "great", emoji: "\u{1F60A}", label: t("tagebuch.super") },
                { key: "good", emoji: "\u{1F642}", label: t("tagebuch.gut") },
                { key: "neutral", emoji: "\u{1F610}", label: t("tagebuch.okay") },
                { key: "low", emoji: "\u{1F614}", label: t("tagebuch.meh") },
                { key: "bad", emoji: "\u{1F622}", label: t("tagebuch.schlecht") },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setSelectedMood(selectedMood === m.key ? null : m.key)}
                  className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl transition-all duration-200 ${
                    selectedMood === m.key
                      ? "bg-[#6b7f4e]/15 scale-110 shadow-sm ring-1 ring-[#6b7f4e]/30"
                      : "hover:bg-[#6b7f4e]/5 hover:scale-105"
                  }`}
                  title={m.label}
                >
                  <span className="text-xl leading-none">{m.emoji}</span>
                  <span className={`text-[9px] font-medium transition-colors ${
                    selectedMood === m.key ? "text-[#6b7f4e]" : "text-[#5a6b4a]/40"
                  }`}>{m.label}</span>
                </button>
              ))}
            </div>

            {/* Action bar */}
            <div className="flex items-center justify-between mx-auto max-w-2xl w-full">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleVoiceToggle}
                  disabled={voice.isTranscribing}
                  className={`p-2.5 rounded-xl transition-all duration-200 ${
                    voice.isRecording
                      ? "bg-red-500 text-white hover:bg-red-600 shadow-sm animate-pulse"
                      : voice.isTranscribing
                        ? "text-[#5a6b4a]/30 cursor-not-allowed"
                        : "bg-[#6b7f4e]/10 text-[#6b7f4e] hover:bg-[#6b7f4e]/20"
                  }`}
                  title={voice.isRecording ? t("coach.recordingRunning") : t("coach.voiceStart")}
                >
                  {voice.isRecording ? (
                    <MicOff className="w-4 h-4" />
                  ) : voice.isTranscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                </button>
                {voice.isRecording && (
                  <span className="text-xs text-red-500/70 font-medium animate-pulse">
                    {t("coach.recordingRunning")} {voice.duration}s
                  </span>
                )}
                {voice.isTranscribing && (
                  <span className="text-xs text-[#6b7f4e]/70 font-medium">{t("coach.recognizing")}</span>
                )}
              </div>

              <button
                onClick={handleSaveEntry}
                disabled={!journalText.trim()}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  journalText.trim()
                    ? "bg-[#6b7f4e] text-white hover:bg-[#5a6e3f] shadow-sm active:scale-95"
                    : "bg-[#5a6b4a]/10 text-[#5a6b4a]/30 cursor-not-allowed"
                }`}
              >
                <Save className="w-4 h-4" />
                {t("tagebuch.speichern")}
              </button>
            </div>

            {(error || voice.error) && (
              <div className="flex justify-center pt-2">
                <div className="flex items-center gap-2 text-xs text-red-500/70 bg-red-900/20 px-3 py-2 rounded-full">
                  <AlertCircle className="w-3 h-3" />
                  {error || voice.error}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* GESPEICHERT — AYU schweigt, Coaching-Button        */}
      {/* ═══════════════════════════════════════════════════ */}
      {viewMode === "saved" && (
        <div className="flex-1 flex flex-col items-center px-4 overflow-y-auto">
          {/* Erfolgs-Bestätigung */}
          <div className="mt-8 mb-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#6b7f4e]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-[#6b7f4e]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#2d3a1e] mb-2">
              {t("tagebuch.eintragGespeichert")}
            </h2>
            <p className="text-sm text-[#5a6b4a] max-w-md">
              {t("tagebuch.eintragGespeichertDesc")}
            </p>
          </div>

          {/* Gespeicherter Eintrag (kompakt) */}
          <div className="bg-[#f0f3eb] rounded-2xl border border-[#d4dcc4] shadow-sm p-5 mb-6 max-w-2xl w-full">
            <div className="flex items-center gap-2 mb-2">
              <PenLine className="w-3.5 h-3.5 text-[#6b7f4e]/50" />
              <span className="text-[10px] font-medium text-[#6b7f4e]/50 uppercase tracking-wider">{t("tagebuch.deinEintrag")}</span>
              <span className="text-[10px] text-[#5a6b4a]/40 ml-auto">{messages.find((m) => m.role === "user")?.time}</span>
            </div>
            <p className="text-sm leading-relaxed text-[#2d3a1e]/70 whitespace-pre-wrap font-['DM_Sans',sans-serif] line-clamp-6">
              {messages.find((m) => m.role === "user")?.text || journalText}
            </p>
          </div>

          {/* ═══ COACHING-BUTTON — prominent ═══ */}
          <button
            onClick={handleRequestCoaching}
            disabled={isTyping}
            className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-[#6b7f4e] to-[#5a6e3f] text-white text-base font-medium hover:from-[#5a6e3f] hover:to-[#4a5e38] shadow-lg hover:shadow-xl transition-all duration-300 mb-6 group disabled:opacity-50"
          >
            <div className="w-8 h-8 rounded-xl bg-[#f8f8f8]/20 flex items-center justify-center group-hover:bg-[#f8f8f8]/30 transition-colors">
              {isTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            </div>
            {isTyping ? t("tagebuch.annamDenkt") : t("tagebuch.coachingBitten")}
          </button>

          {/* Trigger-Buttons */}
          <div className="mb-6">
            <p className="text-[10px] text-[#5a6b4a]/40 text-center mb-3 uppercase tracking-widest">
              {t("tagebuch.inspiriereicht")}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleTrigger("orakel")}
                disabled={isTyping}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#d4dcc4] text-xs text-[#6b7f4e]/60 hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/5 transition-all disabled:opacity-30"
              >
                <Compass className="w-3.5 h-3.5" />
                {t("tagebuch.orakel")}
              </button>
              <button
                onClick={() => handleTrigger("mantra")}
                disabled={isTyping}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#d4dcc4] text-xs text-[#6b7f4e]/60 hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/5 transition-all disabled:opacity-30"
              >
                <Flame className="w-3.5 h-3.5" />
                {t("tagebuch.mantra")}
              </button>
              <button
                onClick={() => handleTrigger("joke")}
                disabled={isTyping}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E8A87C]/15 text-xs text-[#E8A87C]/60 hover:text-[#E8A87C] hover:bg-[#E8A87C]/5 transition-all disabled:opacity-30"
              >
                <SmilePlus className="w-3.5 h-3.5" />
                {t("tagebuch.witz")}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-red-500/70 bg-red-900/20 px-3 py-2 rounded-full">
              <AlertCircle className="w-3 h-3" />
              {error}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* COACHING-MODUS — Chat mit AYU (nur auf Anfrage)   */}
      {/* ═══════════════════════════════════════════════════ */}
      {viewMode === "coaching" && (
        <div className="flex-1 flex flex-col px-4 overflow-hidden">
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-2 scrollbar-thin pt-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.role === "user"
                      ? "bg-[#6b7f4e]/10 text-[#2d3a1e] rounded-br-md"
                      : "bg-[#f0f3eb] border border-[#d4dcc4] text-[#2d3a1e] rounded-bl-md shadow-sm"
                  }`}
                >
                  {msg.role === "ayu" && (
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-[#6b7f4e]" />
                        <span className="text-[10px] font-medium text-[#6b7f4e] uppercase tracking-wider">Annam</span>
                      </div>
                      {tts.isSupported && (
                        <button
                          onClick={() => tts.speak(msg.text, msg.id)}
                          className={`p-1 rounded-lg transition-all duration-200 ${
                            (tts.isSpeaking || tts.isLoading) && tts.currentMsgId === msg.id
                              ? "bg-[#6b7f4e]/15 text-[#6b7f4e]"
                              : "text-[#5a6b4a]/30 hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/5"
                          }`}
                          title={
                            tts.isLoading && tts.currentMsgId === msg.id
                              ? t("common.loading")
                              : tts.isSpeaking && tts.currentMsgId === msg.id
                                ? t("common.close")
                                : t("coach.voiceStart")
                          }
                          disabled={tts.isLoading && tts.currentMsgId !== msg.id}
                        >
                          {tts.isLoading && tts.currentMsgId === msg.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : tts.isSpeaking && tts.currentMsgId === msg.id ? (
                            <VolumeX className="w-3.5 h-3.5" />
                          ) : (
                            <Volume2 className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  {msg.role === "user" && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <PenLine className="w-3 h-3 text-[#6b7f4e]/50" />
                      <span className="text-[10px] font-medium text-[#6b7f4e]/50 uppercase tracking-wider">
                        {msg.text.startsWith("/") ? t("tagebuch.trigger") : t("tagebuch.deinEintrag")}
                      </span>
                    </div>
                  )}
                  <ChatMarkdown text={msg.text} isStreaming={msg.id === "streaming-ayu"} />
                  {/* Rezept speichern Button */}
                  {msg.role === "ayu" && msg.id !== "streaming-ayu" && (
                    <SaveRecipeButton
                      messageText={msg.text}
                      clientId={clientData.clientId || "anon"}
                      messageId={msg.id}
                    />
                  )}
                  <p className={`text-[10px] mt-1.5 ${msg.role === "user" ? "text-[#5a6b4a]/30 text-right" : "text-[#5a6b4a]/25"}`}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && !messages.some(m => m.id === "streaming-ayu" && m.text.length > 0) && (
              <div className="flex justify-start">
                <div className="bg-[#f0f3eb] border border-[#d4dcc4] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Sparkles className="w-3 h-3 text-[#6b7f4e]" />
                    <span className="text-[10px] font-medium text-[#6b7f4e] uppercase tracking-wider">Annam</span>
                  </div>
                  <div className="flex gap-1.5 py-1">
                    <div className="w-2 h-2 rounded-full bg-[#6b7f4e]/30 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 rounded-full bg-[#6b7f4e]/30 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 rounded-full bg-[#6b7f4e]/30 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {(error || voice.error) && (
              <div className="flex justify-center">
                <div className="flex items-center gap-2 text-xs text-red-500/70 bg-red-900/20 px-3 py-2 rounded-full">
                  <AlertCircle className="w-3 h-3" />
                  {error || voice.error}
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Chat Input + Trigger-Buttons */}
          <div className="pt-3 border-t border-[#d4dcc4] mt-2 pb-2 shrink-0">
            <div className="flex gap-1.5 mb-2 px-1">
              <button
                onClick={() => handleTrigger("orakel")}
                disabled={isTyping}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium border border-[#d4dcc4] text-[#6b7f4e]/50 hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/5 transition-all disabled:opacity-30"
              >
                <Compass className="w-3 h-3" />
                {t("tagebuch.orakel")}
              </button>
              <button
                onClick={() => handleTrigger("mantra")}
                disabled={isTyping}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium border border-[#d4dcc4] text-[#6b7f4e]/50 hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/5 transition-all disabled:opacity-30"
              >
                <Flame className="w-3 h-3" />
                {t("tagebuch.mantra")}
              </button>
              <button
                onClick={() => handleTrigger("joke")}
                disabled={isTyping}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium border border-[#E8A87C]/15 text-[#E8A87C]/50 hover:text-[#E8A87C] hover:bg-[#E8A87C]/5 transition-all disabled:opacity-30"
              >
                <SmilePlus className="w-3 h-3" />
                {t("tagebuch.witz")}
              </button>
            </div>

            <div className="flex items-end gap-2 bg-[#f0f3eb] rounded-2xl border border-[#d4dcc4] p-2 focus-within:border-[#6b7f4e]/30 focus-within:shadow-sm transition-all">
              <button
                onClick={handleVoiceToggle}
                disabled={voice.isTranscribing || isTyping}
                className={`p-2 rounded-xl transition-all duration-200 shrink-0 ${
                  voice.isRecording
                    ? "bg-red-500 text-white hover:bg-red-600 shadow-sm animate-pulse"
                    : voice.isTranscribing
                      ? "bg-[#f5f0e8]/5 text-[#5a6b4a]/30 cursor-not-allowed"
                      : "bg-[#6b7f4e]/10 text-[#6b7f4e] hover:bg-[#6b7f4e]/20"
                }`}
                title={voice.isRecording ? t("coach.recordingRunning") : t("coach.voiceStart")}
              >
                {voice.isRecording ? (
                  <MicOff className="w-4 h-4" />
                ) : voice.isTranscribing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>

              <textarea
                ref={chatInputRef}
                value={chatInput}
                onChange={(e) => {
                  setChatInput(e.target.value);
                  // Auto-resize: Textfeld wächst mit dem Inhalt
                  const el = e.target;
                  el.style.height = "auto";
                  el.style.height = Math.min(el.scrollHeight, 128) + "px";
                }}
                onKeyDown={handleChatKeyDown}
                placeholder={voice.isRecording ? t("coach.speakNow") : t("tagebuch.antwortAnnam")}
                rows={1}
                className="flex-1 resize-none bg-transparent text-sm text-[#2d3a1e] placeholder:text-[#5a6b4a]/30 focus:outline-none px-2 py-1.5 max-h-32"
                style={{ minHeight: "36px" }}
                disabled={voice.isRecording}
              />
              <button
                onClick={handleSendChat}
                disabled={!chatInput.trim() || isTyping || voice.isRecording}
                className={`p-2 rounded-xl transition-all duration-200 ${
                  chatInput.trim() && !isTyping && !voice.isRecording
                    ? "bg-[#6b7f4e] text-white hover:bg-[#5a6e3f] shadow-sm"
                    : "bg-[#f5f0e8]/5 text-[#5a6b4a]/30 cursor-not-allowed"
                }`}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* VERLAUF — Seiten zum Blättern                      */}
      {/* ═══════════════════════════════════════════════════ */}
      {viewMode === "history" && (
        <div className="flex-1 flex flex-col px-4 overflow-hidden">
          {/* Monatsarchiv-Leiste */}
          <div className="py-3 shrink-0">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
              {(() => {
                const monthMap = new Map<string, { label: string; firstIdx: number; count: number }>();
                closedSessions.forEach((s, idx) => {
                  const d = parseDbDate(s.createdAt);
                  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                  if (!monthMap.has(key)) {
                    monthMap.set(key, {
                      label: d.toLocaleDateString(locale === "en" ? "en-GB" : "de-DE", { month: "short", year: "numeric" }),
                      firstIdx: idx,
                      count: 0,
                    });
                  }
                  monthMap.get(key)!.count++;
                });
                const currentSession = closedSessions[historyPage];
                const currentKey = currentSession
                  ? `${parseDbDate(currentSession.createdAt).getFullYear()}-${String(parseDbDate(currentSession.createdAt).getMonth() + 1).padStart(2, "0")}`
                  : "";
                return Array.from(monthMap.entries()).map(([key, { label, firstIdx, count }]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setHistoryPage(firstIdx);
                      setViewingSessionId(closedSessions[firstIdx].id);
                      setViewingMessages(null);
                    }}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      key === currentKey
                        ? "bg-[#6b7f4e] text-white shadow-sm"
                        : "bg-[#6b7f4e]/10 text-[#5a6b4a] hover:bg-[#6b7f4e]/20"
                    }`}
                  >
                    {label} ({count})
                  </button>
                ));
              })()}
            </div>
          </div>

          {/* Session Date + Mood */}
          {closedSessions[historyPage] && (
            <div className="text-center mb-4">
              {closedSessions[historyPage].mood && (() => {
                const moodMap: Record<string, { emoji: string; label: string }> = {
                  great: { emoji: "\u{1F60A}", label: t("tagebuch.super") },
                  good: { emoji: "\u{1F642}", label: t("tagebuch.gut") },
                  neutral: { emoji: "\u{1F610}", label: t("tagebuch.okay") },
                  low: { emoji: "\u{1F614}", label: t("tagebuch.meh") },
                  bad: { emoji: "\u{1F622}", label: t("tagebuch.schlecht") },
                };
                const m = moodMap[closedSessions[historyPage].mood!] || { emoji: closedSessions[historyPage].mood, label: "" };
                return (
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-2xl leading-none">{m.emoji}</span>
                    <span className="text-xs text-[#5a6b4a] font-medium">{m.label}</span>
                  </div>
                );
              })()}
              <p className="text-xs text-[#5a6b4a]/60 font-medium">
                {formatSessionDate(closedSessions[historyPage].createdAt, locale, t as (key: string, params?: Record<string, string>) => string)}
              </p>
              {closedSessions[historyPage].title && (
                <p className="text-sm text-[#2d3a1e]/50 mt-1">
                  {closedSessions[historyPage].title}
                </p>
              )}
            </div>
          )}

          {/* Session Messages */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-2 scrollbar-thin">
            {loadSessionQuery.isLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
              </div>
            )}

            {viewingMessages && viewingMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.role === "user"
                      ? "bg-[#6b7f4e]/10 text-[#2d3a1e] rounded-br-md"
                      : "bg-[#f0f3eb] border border-[#d4dcc4] text-[#2d3a1e] rounded-bl-md shadow-sm"
                  }`}
                >
                  {msg.role === "ayu" && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Sparkles className="w-3 h-3 text-[#6b7f4e]" />
                      <span className="text-[10px] font-medium text-[#6b7f4e] uppercase tracking-wider">Annam</span>
                    </div>
                  )}
                  {msg.role === "user" && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <PenLine className="w-3 h-3 text-[#6b7f4e]/50" />
                      <span className="text-[10px] font-medium text-[#6b7f4e]/50 uppercase tracking-wider">{t("tagebuch.deinEintrag")}</span>
                    </div>
                  )}
                  <ChatMarkdown text={msg.text} isStreaming={false} />
                  {/* Rezept speichern Button (auch im Verlauf) */}
                  {msg.role === "ayu" && (
                    <SaveRecipeButton
                      messageText={msg.text}
                      clientId={clientData.clientId || "anon"}
                      messageId={msg.id}
                    />
                  )}
                  <p className={`text-[10px] mt-1.5 ${msg.role === "user" ? "text-[#5a6b4a]/30 text-right" : "text-[#5a6b4a]/25"}`}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}

            {viewingMessages && viewingMessages.length === 0 && (
              <div className="text-center py-8">
                <BookOpen className="w-6 h-6 text-[#5a6b4a]/20 mx-auto mb-2" />
                <p className="text-xs text-[#5a6b4a]/40">{t("tagebuch.keineMeldungen")}</p>
              </div>
            )}
          </div>

          {/* Coaching-Button für alten Eintrag */}
          {viewingMessages && viewingMessages.length > 0 && (
            <div className="pt-3 border-t border-[#d4dcc4] pb-3 shrink-0 flex justify-center">
              <button
                onClick={async () => {
                  const historyMsgs: Message[] = viewingMessages.map((m) => ({
                    id: m.id,
                    role: m.role as "user" | "ayu",
                    text: m.text,
                    time: m.time,
                  }));
                  setMessages(historyMsgs);
                  setViewingSessionId(null);
                  setViewingMessages(null);
                  setViewMode("coaching");

                  if (!clientData.clientId) return;
                  setIsTyping(true);
                  setError(null);
                  try {
                    let diaryCtx = "";
                    if (recentDiaryEntries && recentDiaryEntries.length > 0) {
                      const entrySummaries = recentDiaryEntries.map((e) => {
                        const date = new Date(e.createdAtMs).toLocaleDateString("de-DE", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                        });
                        const moodLabel = e.mood ? ` [Stimmung: ${e.mood}]` : "";
                        return `${date}${moodLabel}: ${e.content}`;
                      });
                      diaryCtx = `\n\n=== TAGEBUCH-EINTRÄGE DER LETZTEN 5 TAGE ===\n${entrySummaries.join("\n\n")}\n=== ENDE TAGEBUCH-KONTEXT ===\n\nAnalysiere besonders den aktuellsten Eintrag. Erkenne Muster und Entwicklungen über die Tage.`;
                    }
                    const enrichedPrompt = clientData.tagebuchPrompt + diaryCtx;
                    const apiMessages = buildApiMessages(enrichedPrompt, historyMsgs, memoryContext as MemoryContextData | undefined, documentSummaries || undefined);
                    apiMessages.push({
                      role: "user",
                      content: "Ich möchte gerne dein Coaching zu meinem Tagebuch-Eintrag. Schau dir an, was ich geschrieben habe, und gib mir eine ehrliche, tiefgründige Rückmeldung basierend auf meiner Anamnese und meinem Profil.",
                    });
                    const streamingMsg: Message = { id: "streaming-ayu", role: "ayu", text: "", time: formatTime() };
                    setMessages((prev) => [...prev, streamingMsg]);
                    streamingAyuMsgRef.current = "";

                    const result = await streamingChat.sendStreaming({
                      messages: apiMessages,
                      clientId: clientData.clientId,
                      chatType: "tagebuch",
                    });
                    setMessages((prev) => {
                      const withoutStreaming = prev.filter((m) => m.id !== "streaming-ayu");
                      return [...withoutStreaming, { id: `ayu-${Date.now()}`, role: "ayu" as const, text: result.content, time: formatTime() }];
                    });
                  } catch (err) {
                    log.error("History coaching error:");
                    setError(t("tagebuch.fehlerCoaching"));
                    setMessages((prev) => prev.filter((m) => m.id !== "streaming-ayu"));
                  } finally {
                    setIsTyping(false);
                  }
                }}
                disabled={isTyping}
                className="flex items-center gap-2.5 px-6 py-3 rounded-2xl bg-gradient-to-r from-[#6b7f4e] to-[#5a6e3f] text-white text-sm font-medium hover:from-[#5a6e3f] hover:to-[#4a5e38] shadow-md hover:shadow-lg transition-all duration-300 group disabled:opacity-50"
              >
                <div className="w-7 h-7 rounded-xl bg-[#f8f8f8]/20 flex items-center justify-center group-hover:bg-[#f8f8f8]/30 transition-colors">
                  {isTyping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
                </div>
                {isTyping ? t("tagebuch.annamDenkt") : t("tagebuch.mitAnnamSprechen")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── Geburtsdatum-Modal ─── */}
      {showBirthdayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[#f0f3eb] rounded-2xl shadow-xl border border-[#d4dcc4] p-6 w-full max-w-sm mx-4 relative">
            <button
              onClick={() => { setShowBirthdayModal(false); setBirthdayInput(""); }}
              className="absolute top-3 right-3 p-1 rounded-lg text-[#5a6b4a]/40 hover:text-[#5a6b4a] hover:bg-[#f5f0e8]/5 transition-all"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-center mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#6b7f4e]/10 flex items-center justify-center mx-auto mb-3">
                <Star className="w-6 h-6 text-[#6b7f4e]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2d3a1e] mb-1">{t("tagebuch.geburtsdatumTitle")}</h3>
              <p className="text-sm text-[#5a6b4a] leading-relaxed">
                {t("tagebuch.geburtsdatumDesc")}
              </p>
            </div>

            <div className="mb-5">
              <label className="block text-xs font-medium text-[#5a6b4a]/60 mb-1.5">{t("tagebuch.geburtsdatumEingeben")}</label>
              <input
                type="date"
                value={birthdayInput}
                onChange={(e) => setBirthdayInput(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-[#d4dcc4] bg-[#f8f8f8] text-[#2d3a1e] text-sm focus:outline-none focus:ring-2 focus:ring-[#6b7f4e]/30 focus:border-[#6b7f4e]/40 transition-all"
              />
            </div>

            <button
              onClick={async () => {
                if (!birthdayInput || !clientData.clientId) return;
                setBirthdaySaving(true);
                try {
                  await saveBirthdateMutation.mutateAsync({
                    clientId: clientData.clientId,
                    birthdate: birthdayInput,
                  });
                  birthdateQuery.refetch();
                  setShowBirthdayModal(false);
                  setBirthdayInput("");
                } catch (err) {
                  log.error("Fehler beim Speichern:");
                } finally {
                  setBirthdaySaving(false);
                }
              }}
              disabled={!birthdayInput || birthdaySaving}
              className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                birthdayInput && !birthdaySaving
                  ? "bg-[#6b7f4e] text-white hover:bg-[#5a6e3f] shadow-sm"
                  : "bg-[#f5f0e8]/5 text-[#5a6b4a]/30 cursor-not-allowed"
              }`}
            >
              {birthdaySaving ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("common.saving")}
                </span>
              ) : (
                t("common.save")
              )}
            </button>

            <p className="text-[10px] text-[#5a6b4a]/40 text-center mt-3">
              {t("tagebuch.geburtsdatumHinweis")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
